/**
 * TabStyleGenerator
 * generiert css für tab-styles
 * muss synchron mit backend DesignController.cs gehalten werden
 */

import { TabLevelColors } from './SimpleDesignConfig';

export const TAB_LEVEL_MAP = {
    'kslplus-haushaltstab-wrapper-top': 'tabTop',
    'kslplus-haushaltstab-wrapper-middle': 'tabMiddle',
    'kslplus-haushaltstab-wrapper-bottom': 'tabBottom',
    'kslplus-haushaltstab-wrapper-very-bottom': 'tabVeryBottom'
} as const;

export type TabLevel = 'tabTop' | 'tabMiddle' | 'tabBottom' | 'tabVeryBottom';

const TAB_WRAPPERS = [
    'kslplus-haushaltstab-wrapper-top',
    'kslplus-haushaltstab-wrapper-middle',
    'kslplus-haushaltstab-wrapper-bottom',
    'kslplus-haushaltstab-wrapper-very-bottom'
];

export interface TabLevelColorsConfig {
    tabTop: TabLevelColors;
    tabMiddle: TabLevelColors;
    tabBottom: TabLevelColors;
    tabVeryBottom: TabLevelColors;
}

// styles die eine sichtbare rahmen-/akzentfarbe verwenden
var STYLES_WITH_BORDER_COLOR: { [key: string]: boolean } = {
    'standard': true,
    'underline': true,
    'pills': true,
    'minimal-pill': true,
    'segment': true,
    'neo-brutal': true,
    'glass-premium': true,
    'neon': true,
    'soft-glow': true,
    'cyber-cut': true,
    'browser': true,
    'folder': true,
    'floating-island': false,
    'macos-dock': false,
    'pill-layer': true
};

export function styleUsesBorderColor(style: string): boolean {
    if (!(style in STYLES_WITH_BORDER_COLOR)) {
        return true;
    }
    return STYLES_WITH_BORDER_COLOR[style];
}

function flatMapArray<T, U>(arr: T[], fn: (item: T) => U[]): U[] {
    var result: U[] = [];
    for (var i = 0; i < arr.length; i++) {
        var mapped = fn(arr[i]);
        for (var j = 0; j < mapped.length; j++) {
            result.push(mapped[j]);
        }
    }
    return result;
}

// generiert css für tab-farben (ebenenspezifisch)
export function generateTabColorsCSS(
    scope: string,
    levelColors: TabLevelColorsConfig,
    websiteBackgroundColor: string,
    borderColor: string
): string {
    var ss = scope + '[class][class]';
    var css = '';

    var wrapperToLevel: { [key: string]: TabLevel } = {
        'kslplus-haushaltstab-wrapper-top': 'tabTop',
        'kslplus-haushaltstab-wrapper-middle': 'tabMiddle',
        'kslplus-haushaltstab-wrapper-bottom': 'tabBottom',
        'kslplus-haushaltstab-wrapper-very-bottom': 'tabVeryBottom'
    };

    for (var i = 0; i < TAB_WRAPPERS.length; i++) {
        var wrapper = TAB_WRAPPERS[i];
        var level = wrapperToLevel[wrapper];
        var colors = levelColors[level];

        css += '\n/* ' + wrapper + ' */\n';

        css += ss + ' .' + wrapper + ' .nav-tabs {\n';
        css += '    background-color: ' + websiteBackgroundColor + ' !important;\n';
        css += '    border: none !important;\n';
        css += '}\n';

        css += ss + ' .' + wrapper + ' .nav-item {\n';
        css += '    background-color: ' + websiteBackgroundColor + ' !important;\n';
        css += '}\n';

        css += ss + ' .' + wrapper + ' .nav-link:not(.active),\n';
        css += ss + ' .' + wrapper + ' .nav-tabs .nav-link:not(.active) {\n';
        css += '    color: ' + colors.inactiveColor + ' !important;\n';
        css += '    background-color: ' + colors.inactiveBgColor + ' !important;\n';
        css += '}\n';

        css += ss + ' .' + wrapper + ' .nav-link,\n';
        css += ss + ' .' + wrapper + ' .nav-tabs .nav-link {\n';
        css += '    color: ' + colors.inactiveColor + ' !important;\n';
        css += '    background-color: ' + colors.inactiveBgColor + ' !important;\n';
        css += '}\n';

        css += ss + ' .' + wrapper + ' .nav-link.active,\n';
        css += ss + ' .' + wrapper + ' .nav-tabs .nav-link.active,\n';
        css += ss + ' .' + wrapper + ' .nav-item .nav-link.active,\n';
        css += ss + ' .' + wrapper + ' .nav-item .active {\n';
        css += '    color: ' + colors.activeColor + ' !important;\n';
        css += '    background-color: ' + colors.activeBgColor + ' !important;\n';
        css += '}\n';

        css += ss + ' .' + wrapper + ' .nav-link:hover,\n';
        css += ss + ' .' + wrapper + ' .nav-tabs .nav-link:hover {\n';
        css += '    color: ' + colors.hoverColor + ' !important;\n';
        css += '    background-color: ' + colors.hoverBgColor + ' !important;\n';
        css += '}\n';
    }

    return css;
}

// generiert css für tab-styles (form & aussehen)
export function getTabStyleCSS(style: string, scope: string, borderColor: string): string {
    var ss = scope + '[class][class]';

    var baseSelectors = flatMapArray(TAB_WRAPPERS, function (w) {
        return [ss + ' .' + w + ' .nav-link'];
    }).join(',\n');

    var activeSelectors = flatMapArray(TAB_WRAPPERS, function (w) {
        return [ss + ' .' + w + ' .nav-link.active'];
    }).join(',\n');

    var navItemActiveSelectors = flatMapArray(TAB_WRAPPERS, function (w) {
        return [ss + ' .' + w + ' .nav-item .active'];
    }).join(',\n');

    var allActiveSelectors = activeSelectors + ',\n' + navItemActiveSelectors;

    var hoverNotActiveSelectors = flatMapArray(TAB_WRAPPERS, function (w) {
        return [ss + ' .' + w + ' .nav-link:not(.active):hover'];
    }).join(',\n');

    var tabsSelectors = flatMapArray(TAB_WRAPPERS, function (w) {
        return [ss + ' .' + w + ' .nav-tabs'];
    }).join(',\n');

    var itemSelectors = flatMapArray(TAB_WRAPPERS, function (w) {
        return [ss + ' .' + w + ' .nav-item'];
    }).join(',\n');

    var wrapperSelectors = flatMapArray(TAB_WRAPPERS, function (w) {
        return [ss + ' .' + w];
    }).join(',\n');

    var layoutCSS = '\n' +
        wrapperSelectors + ' {\n' +
        '    margin-bottom: 25px !important;\n' +
        '    display: block !important;\n' +
        '}\n' +
        ss + ' .' + TAB_WRAPPERS[TAB_WRAPPERS.length - 1] + ' {\n' +
        '    margin-bottom: 0 !important;\n' +
        '}\n';

    var globalReset = layoutCSS + '\n' +
        tabsSelectors + ' {\n' +
        '    display: flex !important;\n' +
        '    flex-wrap: wrap !important;\n' +
        '    border-bottom: none !important;\n' +
        '    overflow: visible !important;\n' +
        '    padding: 10px !important;\n' +
        '    margin: 0 -10px !important;\n' +
        '}\n' +
        itemSelectors + ' {\n' +
        '    background: transparent !important;\n' +
        '    border: none !important;\n' +
        '    margin-bottom: 0 !important;\n' +
        '    overflow: visible !important;\n' +
        '}\n';

    var css = globalReset;

    switch (style) {

        case 'standard':
            var tabWidthRules = '\n';
            var levelConfigs = [
                ['kslplus-haushaltstab-wrapper-top', '95%', '1.5em', '900'],
                ['kslplus-haushaltstab-wrapper-middle', '90%', '1.4em', '700'],
                ['kslplus-haushaltstab-wrapper-bottom', '85%', '1.2em', '600'],
                ['kslplus-haushaltstab-wrapper-very-bottom', '80%', '1.2em', '400']
            ];

            var levelStyleRules = '\n';
            for (var ls = 0; ls < levelConfigs.length; ls++) {
                var lwrap = levelConfigs[ls][0];
                var lfontSize = levelConfigs[ls][2];
                var lfontWeight = levelConfigs[ls][3];

                levelStyleRules += ss + ' .' + lwrap + ' .nav-item {\n';
                levelStyleRules += '    text-align: center !important;\n';
                levelStyleRules += '    font-size: ' + lfontSize + ' !important;\n';
                levelStyleRules += '    font-weight: ' + lfontWeight + ' !important;\n';
                levelStyleRules += '    cursor: pointer !important;\n';
                levelStyleRules += '    margin-bottom: 0.2em !important;\n';
                levelStyleRules += '}\n';

                levelStyleRules += ss + ' .' + lwrap + ' .nav-item:not(:last-child) {\n';
                levelStyleRules += '    margin-right: 5px !important;\n';
                levelStyleRules += '}\n';
            }

            for (var lc = 0; lc < levelConfigs.length; lc++) {
                var wrapperName = levelConfigs[lc][0];
                var widthPct = levelConfigs[lc][1];
                var sel = ss + ' .' + wrapperName + ' .nav-item';

                tabWidthRules += sel + ':nth-child(1):nth-last-child(2),\n';
                tabWidthRules += sel + ':nth-child(2):nth-last-child(1) {\n';
                tabWidthRules += '    min-width: calc((' + widthPct + ' - 5px) / 2) !important;\n}\n';

                tabWidthRules += sel + ':nth-child(1):nth-last-child(3),\n';
                tabWidthRules += sel + ':nth-child(2):nth-last-child(2),\n';
                tabWidthRules += sel + ':nth-child(3):nth-last-child(1) {\n';
                tabWidthRules += '    min-width: calc((' + widthPct + ' - 10px) / 3) !important;\n}\n';

                tabWidthRules += sel + ':nth-child(1):nth-last-child(4),\n';
                tabWidthRules += sel + ':nth-child(2):nth-last-child(3),\n';
                tabWidthRules += sel + ':nth-child(3):nth-last-child(2),\n';
                tabWidthRules += sel + ':nth-child(4):nth-last-child(1) {\n';
                tabWidthRules += '    min-width: calc((' + widthPct + ' - 15px) / 4) !important;\n}\n';

                tabWidthRules += sel + ':nth-child(1):nth-last-child(5),\n';
                tabWidthRules += sel + ':nth-child(2):nth-last-child(4),\n';
                tabWidthRules += sel + ':nth-child(3):nth-last-child(3),\n';
                tabWidthRules += sel + ':nth-child(4):nth-last-child(2),\n';
                tabWidthRules += sel + ':nth-child(5):nth-last-child(1) {\n';
                tabWidthRules += '    min-width: calc((' + widthPct + ' - 20px) / 5) !important;\n}\n';

                tabWidthRules += sel + ':nth-child(1):nth-last-child(6),\n';
                tabWidthRules += sel + ':nth-child(2):nth-last-child(5),\n';
                tabWidthRules += sel + ':nth-child(3):nth-last-child(4),\n';
                tabWidthRules += sel + ':nth-child(4):nth-last-child(3),\n';
                tabWidthRules += sel + ':nth-child(5):nth-last-child(2),\n';
                tabWidthRules += sel + ':nth-child(6):nth-last-child(1) {\n';
                tabWidthRules += '    min-width: calc((' + widthPct + ' - 25px) / 6) !important;\n}\n';

                tabWidthRules += sel + ':nth-child(1):nth-last-child(7),\n';
                tabWidthRules += sel + ':nth-child(2):nth-last-child(6),\n';
                tabWidthRules += sel + ':nth-child(3):nth-last-child(5),\n';
                tabWidthRules += sel + ':nth-child(4):nth-last-child(4),\n';
                tabWidthRules += sel + ':nth-child(5):nth-last-child(3),\n';
                tabWidthRules += sel + ':nth-child(6):nth-last-child(2),\n';
                tabWidthRules += sel + ':nth-child(7):nth-last-child(1) {\n';
                tabWidthRules += '    min-width: calc((' + widthPct + ' - 30px) / 7) !important;\n}\n';
            }

            return layoutCSS + levelStyleRules + tabWidthRules + '\n' +
                baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 5px 15px 5px 5px !important;\n' +
                '    padding: 10px 16px !important;\n' +
                '    margin: 0 5px !important;\n' +
                '    transition: all 0.2s ease !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: solid !important;\n' +
                '    border-color: ' + borderColor + ' !important;\n' +
                '}\n';

        case 'underline':
            return css + '\n' + baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 4px 4px 0 0 !important;\n' +
                '    padding: 12px 18px !important;\n' +
                '    margin: 0 8px !important;\n' +
                '    background: transparent !important;\n' +
                '    position: relative !important;\n' +
                '    background-image: linear-gradient(' + borderColor + ', ' + borderColor + ') !important;\n' +
                '    background-size: 0% 3px !important;\n' +
                '    background-repeat: no-repeat !important;\n' +
                '    background-position: center bottom !important;\n' +
                '    transition: background-size 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s ease !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    background-size: 100% 3px !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    background-color: rgba(0,0,0,0.03) !important;\n' +
                '    background-size: 40% 3px !important;\n' +
                '}\n';

        case 'pills':
            return css + '\n' + baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 50px !important;\n' +
                '    padding: 10px 26px !important;\n' +
                '    margin: 0 8px !important;\n' +
                '    font-weight: 500 !important;\n' +
                '    transition: all 0.2s ease !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    background-color: ' + borderColor + ' !important;\n' +
                '    color: white !important;\n' +
                '    box-shadow: 0 4px 12px ' + borderColor + '60 !important;\n' +
                '    transform: translateY(-2px) !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    transform: translateY(-1px) !important;\n' +
                '    box-shadow: 0 2px 5px rgba(0,0,0,0.05) !important;\n' +
                '}\n';

        case 'minimal-pill':
            return css + '\n' + baseSelectors + ' {\n' +
                '    border: 1px solid #e2e8f0 !important;\n' +
                '    border-radius: 8px !important;\n' +
                '    padding: 8px 22px !important;\n' +
                '    margin: 0 6px !important;\n' +
                '    font-weight: 500 !important;\n' +
                '    transition: all 0.2s ease !important;\n' +
                '    background: white !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: 1px solid ' + borderColor + ' !important;\n' +
                '    background-color: ' + borderColor + '0D !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    transform: translateY(-1px) !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    border: 1px solid #cbd5e1 !important;\n' +
                '    transform: translateY(-1px) !important;\n' +
                '}\n';

        case 'segment':
            return css + '\n' + tabsSelectors + ' {\n' +
                '    background: #f1f5f9 !important;\n' +
                '    border-radius: 12px !important;\n' +
                '    padding: 6px !important;\n' +
                '    display: flex !important;\n' +
                '    flex-wrap: wrap !important;\n' +
                '    border: 1px solid #e2e8f0 !important;\n' +
                '    box-shadow: inset 0 2px 4px rgba(0,0,0,0.03) !important;\n' +
                '    gap: 4px !important;\n' +
                '}\n' +
                baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 8px !important;\n' +
                '    padding: 8px 24px !important;\n' +
                '    margin: 0 !important;\n' +
                '    text-align: center !important;\n' +
                '    font-weight: 500 !important;\n' +
                '    color: #64748b !important;\n' +
                '    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;\n' +
                '    flex: 1 1 auto !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    background: white !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    box-shadow: 0 2px 8px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04) !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    background: rgba(255,255,255,0.5) !important;\n' +
                '    color: #334155 !important;\n' +
                '}\n';

        case 'neo-brutal':
            return css + '\n' + baseSelectors + ' {\n' +
                '    border: 2px solid #000000 !important;\n' +
                '    border-radius: 6px !important;\n' +
                '    padding: 10px 24px !important;\n' +
                '    margin: 0 12px 8px 0 !important;\n' +
                '    font-weight: 700 !important;\n' +
                '    background: white !important;\n' +
                '    box-shadow: 4px 4px 0px 0px #000000 !important;\n' +
                '    transform: translate(-2px, -2px) !important;\n' +
                '    transition: all 0.15s ease !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: 2px solid #000000 !important;\n' +
                '    background: ' + borderColor + ' !important;\n' +
                '    color: white !important;\n' +
                '    box-shadow: 0px 0px 0px 0px #000000 !important;\n' +
                '    transform: translate(2px, 2px) !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    box-shadow: 6px 6px 0px 0px #000000 !important;\n' +
                '    transform: translate(-4px, -4px) !important;\n' +
                '}\n';

        case 'glass-premium':
            return css + '\n' + baseSelectors + ' {\n' +
                '    background: rgba(255, 255, 255, 0.3) !important;\n' +
                '    backdrop-filter: blur(12px) !important;\n' +
                '    -webkit-backdrop-filter: blur(12px) !important;\n' +
                '    border: 1px solid rgba(255, 255, 255, 0.6) !important;\n' +
                '    border-bottom: 1px solid rgba(255, 255, 255, 0.3) !important;\n' +
                '    border-radius: 12px !important;\n' +
                '    padding: 12px 28px !important;\n' +
                '    margin: 0 8px !important;\n' +
                '    box-shadow: 0 4px 15px rgba(0,0,0,0.05) !important;\n' +
                '    transition: all 0.3s ease !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    background: rgba(255, 255, 255, 0.85) !important;\n' +
                '    border: 1px solid ' + borderColor + ' !important;\n' +
                '    box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15), inset 0 0 0 1px rgba(255,255,255,0.8) !important;\n' +
                '    transform: translateY(-3px) !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '    font-weight: 600 !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    background: rgba(255, 255, 255, 0.5) !important;\n' +
                '    transform: translateY(-1px) !important;\n' +
                '}\n';

        case 'neon':
            return css + '\n' + tabsSelectors + ' {\n' +
                '    padding: 10px !important;\n' +
                '}\n' +
                baseSelectors + ' {\n' +
                '    border: 1px solid transparent !important;\n' +
                '    border-radius: 4px !important;\n' +
                '    padding: 10px 24px !important;\n' +
                '    margin: 0 6px !important;\n' +
                '    background: transparent !important;\n' +
                '    transition: all 0.3s ease !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '    opacity: 0.8 !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: 1px solid ' + borderColor + ' !important;\n' +
                '    box-shadow: 0 0 10px ' + borderColor + ', inset 0 0 10px ' + borderColor + '20 !important;\n' +
                '    text-shadow: 0 0 8px ' + borderColor + '80 !important;\n' +
                '    opacity: 1 !important;\n' +
                '    background: rgba(255,255,255,0.05) !important;\n' +
                '    font-weight: 600 !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    text-shadow: 0 0 5px ' + borderColor + '40 !important;\n' +
                '    opacity: 1 !important;\n' +
                '    background: rgba(255,255,255,0.02) !important;\n' +
                '}\n';

        case 'soft-glow':
            return css + '\n' + baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 30px !important;\n' +
                '    padding: 10px 26px !important;\n' +
                '    margin: 0 8px !important;\n' +
                '    background: white !important;\n' +
                '    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '    box-shadow: 0 10px 25px -5px ' + borderColor + '80, 0 5px 10px -5px ' + borderColor + '40 !important;\n' +
                '    transform: translateY(-2px) !important;\n' +
                '    font-weight: 600 !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    transform: translateY(-1px) !important;\n' +
                '    box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;\n' +
                '}\n';

        case 'cyber-cut':
            return css + '\n' + baseSelectors + ' {\n' +
                '    border: 1px solid ' + borderColor + '60 !important;\n' +
                '    border-radius: 0 !important;\n' +
                '    padding: 12px 28px !important;\n' +
                '    margin: 0 5px !important;\n' +
                '    background: transparent !important;\n' +
                '    clip-path: polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px) !important;\n' +
                '    text-transform: uppercase !important;\n' +
                '    letter-spacing: 1.5px !important;\n' +
                '    font-size: 0.85em !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    transition: all 0.2s ease !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    background: ' + borderColor + ' !important;\n' +
                '    color: white !important;\n' +
                '    border: 1px solid ' + borderColor + ' !important;\n' +
                '    box-shadow: 0 0 20px ' + borderColor + '50 !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    background: ' + borderColor + '10 !important;\n' +
                '}\n';

        case 'browser':
            return css + '\n' + tabsSelectors + ' {\n' +
                '    border-bottom: 2px solid ' + borderColor + ' !important;\n' +
                '    padding-bottom: 0 !important;\n' +
                '    overflow: visible !important;\n' +
                '}\n' +
                baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 8px 8px 0 0 !important;\n' +
                '    padding: 12px 30px !important;\n' +
                '    margin: 0 -15px !important;\n' +
                '    background: #e2e8f0 !important;\n' +
                '    color: #64748b !important;\n' +
                '    clip-path: polygon(15px 0, calc(100% - 15px) 0, 100% 100%, 0 100%) !important;\n' +
                '    transition: all 0.2s ease !important;\n' +
                '    width: auto !important;\n' +
                '    min-width: 130px !important;\n' +
                '    text-align: center !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    background: ' + borderColor + ' !important;\n' +
                '    color: white !important;\n' +
                '    z-index: 10 !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    box-shadow: 0 -4px 12px rgba(0,0,0,0.1) !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    background: #cbd5e1 !important;\n' +
                '    z-index: 5 !important;\n' +
                '}\n';

        case 'folder':
            return css + '\n' + tabsSelectors + ' {\n' +
                '    border-bottom: 1px solid #ced4da !important;\n' +
                '}\n' +
                baseSelectors + ' {\n' +
                '    border: 1px solid #ced4da !important;\n' +
                '    border-bottom: none !important;\n' +
                '    border-radius: 6px 6px 0 0 !important;\n' +
                '    padding: 12px 28px !important;\n' +
                '    margin: 0 0 0 -4px !important;\n' +
                '    background: #f8f9fa !important;\n' +
                '    position: relative !important;\n' +
                '    transition: all 0.15s ease !important;\n' +
                '    top: 0 !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    background: white !important;\n' +
                '    border: 1px solid #ced4da !important;\n' +
                '    border-top: 3px solid ' + borderColor + ' !important;\n' +
                '    border-bottom: 1px solid white !important;\n' +
                '    padding-bottom: 13px !important;\n' +
                '    margin-bottom: -1px !important;\n' +
                '    top: -2px !important;\n' +
                '    box-shadow: 0 -2px 5px rgba(0,0,0,0.05) !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    z-index: 5 !important;\n' +
                '}\n';

        case 'floating-island':
            return css + '\n' + tabsSelectors + ' {\n' +
                '    background: white !important;\n' +
                '    border: 1px solid #f1f5f9 !important;\n' +
                '    border-radius: 16px !important;\n' +
                '    padding: 8px !important;\n' +
                '    display: flex !important;\n' +
                '    flex-wrap: wrap !important;\n' +
                '    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05) !important;\n' +
                '    gap: 8px !important;\n' +
                '}\n' +
                baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 10px !important;\n' +
                '    padding: 8px 22px !important;\n' +
                '    margin: 0 !important;\n' +
                '    background: transparent !important;\n' +
                '    color: #64748b !important;\n' +
                '    transition: all 0.2s ease !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    background: #f8fafc !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    box-shadow: 0 2px 4px rgba(0,0,0,0.03) !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    background: #f8fafc !important;\n' +
                '}\n';

        case 'macos-dock':
            return css + '\n' + tabsSelectors + ' {\n' +
                '    background: rgba(255, 255, 255, 0.6) !important;\n' +
                '    backdrop-filter: blur(20px) !important;\n' +
                '    -webkit-backdrop-filter: blur(20px) !important;\n' +
                '    border: 1px solid rgba(255, 255, 255, 0.5) !important;\n' +
                '    border-radius: 24px !important;\n' +
                '    padding: 8px 12px !important;\n' +
                '    display: flex !important;\n' +
                '    flex-wrap: wrap !important;\n' +
                '    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.1) !important;\n' +
                '    gap: 6px !important;\n' +
                '}\n' +
                baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 16px !important;\n' +
                '    padding: 10px 22px !important;\n' +
                '    margin: 0 !important;\n' +
                '    background: transparent !important;\n' +
                '    color: #475569 !important;\n' +
                '    transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1) !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    background: white !important;\n' +
                '    color: black !important;\n' +
                '    font-weight: 500 !important;\n' +
                '    box-shadow: 0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.02) !important;\n' +
                '    transform: scale(1.05) translateY(-2px) !important;\n' +
                '}\n' +
                hoverNotActiveSelectors + ' {\n' +
                '    background: rgba(255,255,255,0.5) !important;\n' +
                '    transform: scale(1.05) translateY(-2px) !important;\n' +
                '}\n';

        case 'pill-layer':
            return css + '\n' + baseSelectors + ' {\n' +
                '    border: 2px solid transparent !important;\n' +
                '    border-radius: 99px !important;\n' +
                '    padding: 10px 26px !important;\n' +
                '    margin: 0 8px !important;\n' +
                '    background: white !important;\n' +
                '    transition: all 0.25s ease !important;\n' +
                '    box-shadow: 0 2px 4px rgba(0,0,0,0.03) !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: 2px solid transparent !important;\n' +
                '    color: ' + borderColor + ' !important;\n' +
                '    box-shadow: 0 0 0 3px white, 0 0 0 5px ' + borderColor + ' !important;\n' +
                '    transform: translateY(-1px) !important;\n' +
                '    font-weight: 600 !important;\n' +
                '    background: #f8fafc !important;\n' +
                '}\n';

        default:
            return layoutCSS + '\n' + baseSelectors + ' {\n' +
                '    border: none !important;\n' +
                '    border-radius: 5px 15px 5px 5px !important;\n' +
                '    padding: 10px 16px !important;\n' +
                '    margin: 0 5px !important;\n' +
                '}\n' +
                allActiveSelectors + ' {\n' +
                '    border: solid !important;\n' +
                '    border-color: ' + borderColor + ' !important;\n' +
                '}\n';
    }
}

export var AVAILABLE_TAB_STYLES = [
    'standard',
    'underline',
    'pills',
    'minimal-pill',
    'segment',
    'neo-brutal',
    'glass-premium',
    'neon',
    'soft-glow',
    'cyber-cut',
    'browser',
    'folder',
    'floating-island',
    'macos-dock',
    'pill-layer'
];
